<script setup lang="ts">
import type { OrganizationMember } from "@/model/app/organization-member"
import type { OrganizationTeam } from "@/model/app/organization-team"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import { defineTranslations } from "@/utils/defineTranslations"
import { $date, useHttpClient } from "@/vf"
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { TaskViewMode } from "../../Issue/util"
import {
    type ChampionshipRegistrationTeamMember,
    type ChampionshipRegistrationTeamValidationResult,
    useChampionshipRegistrationSelectTeamData,
    type ValidationResultBlocker,
} from "../championship-registration"

const props = defineProps<{
    /** The category id the team is registered for (used for validation) */
    categoryId: string

    modelValue: ChampionshipRegistrationTeamMember[]

    /** Is submit running? */
    submitting: boolean

    /**
     * The team that is being edited, if any. Used for validation and to display the member count above the columns.
     */
    team?: ChampionshipRegistrationTeam
}>()

const emit = defineEmits<{
    (e: "save"): void
    (e: "update:modelValue", value: ChampionshipRegistrationTeamMember[]): void
}>()

const data = useChampionshipRegistrationSelectTeamData()
const viewMode = inject<"customer" | "admin">(TaskViewMode, "customer")

const http = useHttpClient()
const { t } = useI18n()
const selectedMembers = ref<{
    participant: string[]
    coach: string[]
    supervisor: string[]
    alternate: string[]
}>({ participant: [], coach: [], supervisor: [], alternate: [] })
const errorMessage = ref("")
const columns = [
    ["participant", t("@tasks:tasks.championship_registration.team_list.participants")],
    ["alternate", t("@tasks:tasks.championship_registration.team_list.alternates")],
    ["coach", t("@tasks:tasks.championship_registration.team_list.coaches")],
    ["supervisor", t("@tasks:tasks.championship_registration.team_list.supervisors")],
] as const

type ColumnType = (typeof columns)[number][0]

/**
 * Render the label displayed above the columns for each type of member.
 */
function getColumnCountLabel(column: ColumnType): string {
    if (!props.team) {
        return ""
    }

    const { total, named } = getColumnNamedAndTotalMembersCount(column)

    switch (column) {
        case "participant":
            return t("@tasks:tasks.championship_registration.team_list.number_of_participants", total, {
                named: {
                    total,
                    named,
                },
            })
        case "alternate":
            return t("@tasks:tasks.championship_registration.team_list.number_of_alternates", total, {
                named: {
                    total,
                    named,
                },
            })
        case "coach":
            return t("@tasks:tasks.championship_registration.team_list.coaches", total)
        case "supervisor":
            return t("@tasks:tasks.championship_registration.team_list.supervisors", total)
    }
}

/**
 * Returns the class applied to the column label, depending on the number of named members.
 * If there are more named members than the total number, the class "text-danger" is returned.
 */
function getColumnLabelClass(column: ColumnType): string {
    const { total, named } = getColumnNamedAndTotalMembersCount(column)

    return total > 0 && named > total ? "text-danger" : ""
}

function getColumnNamedAndTotalMembersCount(column: ColumnType): { total: number; named: number } {
    let total: number = 0
    let named: number = 0

    switch (column) {
        case "participant":
            total = props.team?.numberOfParticipants ?? 0
            named = selectedMembers.value.participant.length
            break
        case "alternate":
            total = props.team?.numberOfAlternates ?? 0
            named = selectedMembers.value.alternate.length
            break
        case "coach":
            total = props.team?.numberOfCoaches ?? 0
            named = selectedMembers.value.coach.length
            break
        case "supervisor":
            total = props.team?.numberOfSupervisors ?? 0
            named = selectedMembers.value.supervisor.length
            break
    }

    return { total, named }
}

const entouragesCount = computed<{ total: number; named: number }>(() => {
    const coaches = getColumnNamedAndTotalMembersCount("coach")
    const supervisors = getColumnNamedAndTotalMembersCount("supervisor")

    return {
        total: props.team?.numberOfEntourages ?? 0,
        named: coaches.named + supervisors.named,
    }
})

/*─────────────────────────────────────┐
│  sync modelValue to internal value   │
└─────────────────────────────────────*/
// load team from props for edit
if (props.modelValue) {
    selectedMembers.value = {
        participant: props.modelValue.filter(x => x.type === "participant").map(x => x.memberId),
        coach: props.modelValue.filter(x => x.type === "coach").map(x => x.memberId),
        supervisor: props.modelValue.filter(x => x.type === "supervisor").map(x => x.memberId),
        alternate: props.modelValue.filter(x => x.type === "alternate").map(x => x.memberId),
    }
}

const selectedMembersFlat = computed(() => [
    ...selectedMembers.value.participant.map(
        x => ({ memberId: x, type: "participant" } as ChampionshipRegistrationTeamMember),
    ),
    ...selectedMembers.value.coach.map(x => ({ memberId: x, type: "coach" } as ChampionshipRegistrationTeamMember)),
    ...selectedMembers.value.supervisor.map(
        x => ({ memberId: x, type: "supervisor" } as ChampionshipRegistrationTeamMember),
    ),
    ...selectedMembers.value.alternate.map(
        x => ({ memberId: x, type: "alternate" } as ChampionshipRegistrationTeamMember),
    ),
])

watch(selectedMembersFlat, () => {
    emit("update:modelValue", selectedMembersFlat.value)
})

/*─────────────────────────────────────┐
│  submit                              │
└─────────────────────────────────────*/
async function submit() {
    emit("save")
    return
}

/*─────────────────────────────────────┐
│  validation / blockers               │
└─────────────────────────────────────*/
const validationResult = ref<ChampionshipRegistrationTeamValidationResult | null>(null)

function memberHasBlockersForColumn(column: ColumnType, member: OrganizationMember): boolean {
    return (
        validationResult.value?.availableMembers
            .find(m => m.organizationMemberId === member.id)
            ?.blockers.some(b => b.columns.includes(column)) ?? false
    )
}

function selectionChanged() {
    // Allow members to be in multiple columns.
    // v-model already updates the array for the specific column, so we only need to re-run validation.
    validate()
}

async function validate() {
    const result = await http.post<ChampionshipRegistrationTeamValidationResult>(
        `/championship/registration/${data.value?.registrationId}/select-team/validate?viewMode=${viewMode}`,
        {
            members: selectedMembersFlat.value,
            categoryId: props.categoryId,
            teamId: props.team?.id,
        },
    )
    validationResult.value = result.data
    console.log("validation result", validationResult.value)
}

// contains all blockers, keyed by member id
const memberBlockers = computed(() => {
    const blockers: Record<string, ValidationResultBlocker[]> = {}

    if (!validationResult.value) {
        return blockers
    }

    for (const item of validationResult.value.availableMembers ?? []) {
        blockers[item.organizationMemberId] = item.blockers
    }

    return blockers
})

// contains blockers for the whole team
const teamBlockers = computed(() => validationResult.value?.blockers?.filter(b => !b.memberId).map(b => b.type) ?? null)

watch(selectedMembers, () => {
    validate()
})

if (props.modelValue) {
    await validate()
}

/*─────────────────────────────────────┐
│  Select saved team                   │
└─────────────────────────────────────*/
const selectedTeam = ref<OrganizationTeam | null>(null)

interface FilteredOutMember {
    member: OrganizationMember
    blockers: ValidationResultBlocker[]
}

const filteredOutMembers = ref<FilteredOutMember[]>([])
const showFilteredOutAlert = ref(true)

function memberExistsInAvailableMembers(member: OrganizationMember): boolean {
    return data.value!.availableMembers.some(m => m.id === member.id)
}

function applySavedTeam() {
    const team = selectedTeam.value!
    const filtered = new Map<string, FilteredOutMember>()
    const result: typeof selectedMembers.value = { participant: [], coach: [], supervisor: [], alternate: [] }

    const columnMapping: Array<[ColumnType, OrganizationMember[] | undefined]> = [
        ["participant", team.participants],
        ["coach", team.coaches],
        ["supervisor", team.supervisors],
        ["alternate", team.alternates],
    ]

    for (const [column, members] of columnMapping) {
        for (const member of members ?? []) {
            if (!memberExistsInAvailableMembers(member)) {
                continue
            }

            if (memberHasBlockersForColumn(column, member)) {
                filtered.set(member.id!, { member, blockers: memberBlockers.value[member.id!] || [] })
            } else {
                result[column].push(member.id!)
            }
        }
    }

    selectedMembers.value = result
    filteredOutMembers.value = Array.from(filtered.values()).sort((a, b) => {
        const aName = `${a.member.firstName} ${a.member.lastName}`
        const bName = `${b.member.firstName} ${b.member.lastName}`
        return aName.localeCompare(bName)
    })

    if (filteredOutMembers.value.length > 0) {
        showFilteredOutAlert.value = true
    }
}

/*─────────────────────────────────────┐
│  search                              │
└─────────────────────────────────────*/
const searchValue = ref("")

const filteredMembers = computed(() => {
    return data.value?.availableMembers
        .filter(m => {
            const firstLast = `${m.firstName} ${m.lastName}`.toLowerCase()
            const lastFirst = `${m.lastName} ${m.firstName}`.toLowerCase()

            return (
                firstLast.includes(searchValue.value.toLowerCase()) ||
                lastFirst.includes(searchValue.value.toLowerCase())
            )
        })
        .sort((a, b) => {
            const aBlockers = validationResult.value?.availableMembers.find(
                x => x.organizationMemberId === a.id,
            )?.blockers
            const bBlockers = validationResult.value?.availableMembers.find(
                x => x.organizationMemberId === b.id,
            )?.blockers

            // only push members with blocker type "NotReviewed" to the end
            const aNotReviewed = aBlockers?.some(b => b.type === "NotReviewed") ?? false
            const bNotReviewed = bBlockers?.some(b => b.type === "NotReviewed") ?? false

            if (aNotReviewed && !bNotReviewed) {
                return 1
            }

            if (!aNotReviewed && bNotReviewed) {
                return -1
            }

            // otherwise sort by name
            const aFullName = `${a.firstName} ${a.lastName}`
            const bFullName = `${b.firstName} ${b.lastName}`

            return aFullName.localeCompare(bFullName)
        })
})

const trans = defineTranslations({
    members: {
        de: "Mitglieder",
        en: "Members",
    },
    team: {
        de: "Team",
        en: "Team",
    },
    submit: {
        de: "Jetzt anmelden",
        en: "Register now",
    },
    submit_incomplete: {
        de: "Unvollständig anmelden",
        en: "Register incomplete",
    },
    filtered_members_title: {
        de: 'Nicht angewendete Mitglieder aus Team "{teamName}":',
        en: 'Members not applied from team "{teamName}":',
    },
    filtered_members_not_available: {
        de: "nicht verfügbar",
        en: "not available",
    },
})
</script>

<template>
    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
    <div class="py-2 px-3 d-flex align-items-center text-white bg-dark">
        <div class="d-flex align-items-center">
            <div>{{ trans("members") }}</div>
            <div class="form-inline mx-2">
                <select class="form-control form-inline" v-model="selectedTeam">
                    <option disabled selected>{{ trans("team") }}</option>
                    <option :value="team" v-for="team in data?.availableTeams" :key="team.id">{{ team.name }}</option>
                </select>
            </div>
            <button class="btn btn-primary" type="button" @click="applySavedTeam()" :disabled="!selectedTeam">
                {{ $t("@championship:registration.window.edit_team.applyFilter") }}
            </button>
        </div>
        <div class="flex-grow-1">
            <div class="search">
                <div class="d-flex">
                    <div class="search-icon">
                        <i class="fas fa-fw fa-search"></i>
                    </div>
                    <input
                        v-model="searchValue"
                        type="text"
                        class="form-inline search-field flex-grow-1"
                        :placeholder="$t('crud:list.search')"
                    />
                </div>
            </div>
        </div>
        <div class="d-flex flex-column">
            <button
                :class="{ 'btn-primary': teamBlockers?.length === 0, 'btn-danger': teamBlockers?.length! > 0 }"
                class="btn mt-3"
                :disabled="submitting"
                @click="submit()"
            >
                <slot name="submitLabel" :incomplete="teamBlockers?.length! > 0">
                    {{ trans(teamBlockers?.length === 0 ? "submit" : "submit_incomplete") }}
                </slot>
            </button>
        </div>
    </div>
    <div
        class="alert alert-info mb-0 d-flex align-items-start"
        v-if="filteredOutMembers.length > 0 && showFilteredOutAlert"
    >
        <div class="flex-grow-1">
            <p class="mb-2">
                <strong>{{ trans("filtered_members_title", { teamName: selectedTeam?.name }) }}</strong>
            </p>
            <ul class="mb-0">
                <li v-for="item in filteredOutMembers" :key="item.member.id" class="mb-2">
                    <div>
                        <strong>{{ item.member.firstName }} {{ item.member.lastName }}</strong>
                        <span class="text-muted" v-if="!item.blockers.length">
                            ({{ trans("filtered_members_not_available") }})
                        </span>
                    </div>
                    <div class="small text-muted" v-if="item.blockers.length > 0">
                        <div v-for="blocker in item.blockers" :key="blocker.type">
                            {{ $t("@championship:registration.blocker." + blocker.type) }}
                        </div>
                    </div>
                </li>
            </ul>
        </div>
        <i class="fa fa-fw fa-times ms-2 c-pointer" @click="showFilteredOutAlert = false"></i>
    </div>
    <div class="alert alert-danger border-top-0 mb-0" v-for="m in teamBlockers" :key="m">
        {{ $t("@championship:registration.blocker." + m) }}
    </div>
    <table class="table">
        <thead>
            <tr v-if="props.team">
                <th colspan="5"></th>
                <th colspan="2">
                    {{
                        $t(
                            "@tasks:tasks.championship_registration.team_list.number_of_entourages",
                            entouragesCount.total,
                            {
                                named: {
                                    total: entouragesCount.total,
                                    named: entouragesCount.named,
                                },
                            },
                        )
                    }}
                </th>
            </tr>
            <tr>
                <th colspan="3"></th>
                <th v-for="column in columns" :key="column[0]" :class="getColumnLabelClass(column[0])">
                    {{ getColumnCountLabel(column[0]) || column[1] }}
                </th>
            </tr>
            <tr>
                <th class="align-bottom">{{ $t("@app:organization_member.name") }}</th>
                <th class="align-bottom">{{ $t("@app:organization_member.birthDate") }}</th>
                <th class="align-bottom">{{ $t("@app:organization_member.gender") }}</th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="member in filteredMembers" :key="member.id">
                <td class="text-nowrap">
                    <i class="fa fa-fw fa-mars text-muted" v-if="member.genderForChampionship === 'm'"></i>
                    <i class="fa fa-fw fa-venus text-muted" v-if="member.genderForChampionship === 'f'"></i>
                    {{ member.firstName }} {{ member.lastName }}
                    <div class="text-danger" v-for="blocker in memberBlockers[member.id!] || []" :key="blocker.type">
                        {{ $t("@championship:registration.blocker." + blocker.type) }}
                        <!-- <span class="small text-muted" v-if="blocker.message">({{ blocker.message }})</span> -->
                    </div>
                </td>
                <td>{{ $date(member.birthDate) }}</td>
                <td>{{ $t("crud:gender." + member.gender) }}</td>
                <td v-for="column in columns" :key="column[0]">
                    <input
                        v-model="selectedMembers[column[0]]"
                        type="checkbox"
                        class="custom-checkbox"
                        :class="{
                            'checkbox-office-warning':
                                viewMode === 'admin' && memberHasBlockersForColumn(column[0], member),
                        }"
                        :value="member.id"
                        :disabled="( viewMode === 'customer' && memberHasBlockersForColumn(column[0], member) ) && !selectedMembers[column[0]].includes(member.id!)"
                        @change="selectionChanged()"
                    />
                </td>
            </tr>
        </tbody>
    </table>
</template>

<style scoped>
.checkbox-office-warning {
    background-color: #fd6e6e !important;
}
</style>
